/**
 * Coffin nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coffinNailDesignsPack: ToilePack = {
    slug: 'coffin-nail-designs',
    category: 'beauty',
    name: 'Coffin nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'nude', label: 'Nude coffin', subject: 'long coffin nails in a glossy nude pink with the flat tip squared clean'},
        {key: 'glitter', label: 'Glitter fade', subject: 'coffin nails fading from clear at the base to silver glitter at the tip'},
        {key: 'marble', label: 'Marble coffin', subject: 'coffin nails in white marble with grey veins and a thin gold line along one edge'},
    ],
    styles: [
        {key: 'salon', label: 'Salon close-up', style: 'nail salon close-up in soft diffuse daylight from camera-left, a gentle shadow under the fingers, a bright airy balance, the tapered sides and flat tips seen clearly', palette: 'White and pale grey ground, the color carried on the nails', framing: 'the hand resting flat on white marble, fingertips toward the top of a vertical frame'},
        {key: 'iced', label: 'Iced coffee', style: 'lifestyle photo in bright daylight from camera-right, crisp shadows and condensation glints, a fresh mid-tone balance', palette: 'Caramel coffee and clear glass around the hand, the nails the boldest detail', framing: 'the hand holding a tall glass of iced coffee on a café table, the background softly out of focus'},
        {key: 'satin', label: 'Blush satin', style: 'beauty editorial in soft light from above, shadow gathering in the folds, a soft rosy balance with the nails catching the light', palette: 'Blush satin all around, the nails clear against it', framing: 'the hand laid across draped blush satin filling the frame, the long tips fanned out'},
    ],
    directions: ['ombre pink', 'black matte', 'with rhinestones'],
    directionHint: 'try: ombre pink',
    seo: {
        title: 'Coffin Nail Designs: Nude, Glitter Fade, Marble',
        metaDescription: 'Nine coffin nail designs: a glossy nude coffin, a clear to silver glitter fade and white marble with grey veins, in three settings.',
        h1: 'Coffin nail designs: nude, glitter fade and white marble',
        tagline: 'Nine coffin sets: three long shapes, each shown in a salon close-up, with iced coffee and on blush satin.',
        intro: 'Coffin nail designs for people who want the coffin or ballerina shape, long and tapered with a flat tip. The three designs are long coffin nails in glossy nude pink with a squared tip, nails fading from clear at the base to silver glitter at the tip, and white marble with grey veins and a thin gold line. Type a different color or stone and the same flat-tipped shape carries it.',
        useCases: [
            'a long nude set for the office',
            'a glitter fade for a party',
            'a marble set with a gold edge',
            'a long flat-tipped shape for a first long set',
        ],
        faq: [
            {q: 'What is the coffin shape?', a: 'A long nail with a flat, squared tip and tapered sides, also called ballerina.'},
            {q: 'How does the glitter fade work?', a: 'The nail is clear at the base and turns to silver glitter at the tip.'},
            {q: 'Can I ask for a different shape?', a: 'Yes. A shape you type is a request, not one of the nine pictures shown, which are all coffin nails.'},
        ],
    },
}
